import { useEffect, useState } from "react";

export interface IntervalTimers {
  setInterval: (callback: () => void, ms: number) => number;
  clearInterval: (id: number) => void;
}

const windowTimers: IntervalTimers = {
  setInterval: (callback, ms) => window.setInterval(callback, ms),
  clearInterval: (id) => window.clearInterval(id),
};

/**
 * The effect body of `useNow`, kept apart so its lifecycle is testable without
 * a DOM. Inactive, it schedules nothing. Active, it catches the clock up once
 * (the component may have mounted long before it started showing a time) and
 * then re-reads it every `intervalMs`. The returned cleanup — which React runs
 * on unmount and whenever `active` flips off — clears the interval.
 */
export function startNowTicker(
  active: boolean,
  intervalMs: number,
  onTick: (now: number) => void,
  timers: IntervalTimers = windowTimers,
): (() => void) | undefined {
  if (!active) return undefined;
  onTick(Date.now());
  const id = timers.setInterval(() => onTick(Date.now()), intervalMs);
  return () => timers.clearInterval(id);
}

/** `Date.now()`, re-read every `intervalMs` while `active`; inactive, it keeps
 * the last reading and schedules nothing. Call it from the smallest component
 * that shows the time, so a tick re-renders that leaf and nothing around it. */
export function useNow(intervalMs: number, active = true): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => startNowTicker(active, intervalMs, setNow), [active, intervalMs]);
  return now;
}
